import camelCase from "lodash/camelCase";
import { i18n, TFunction } from "i18next";
import {
  DashBaseFeaturesEnum,
  ProductSubscriptionModel,
  ProductSubscriptionStatusesEnum,
  SUBSCRIPTION_ACTIVE_STATUSES,
  SubscriptionProductNamesEnum,
} from "dash-common";
import { BadgeTypes } from "dash-ui";
import { SubscriptionProduct } from "gql/generated.graphql";

import {
  MAX_FEATURE_ITEMS_NUMBER,
  SHOW_SEE_MORE_FROM_ITEMS_NUMBER,
} from "./ProductCardItem.constants";

export const getFeatureLabels = (
  product: SubscriptionProduct,
  { t, i18n }: { t: TFunction; i18n: i18n }
) => {
  const planMap = {
    [SubscriptionProductNamesEnum.BASIC]: "basic",
    [SubscriptionProductNamesEnum.PRO]: "pro",
    [SubscriptionProductNamesEnum.EXCHANGE]: "exchange",
    [SubscriptionProductNamesEnum.API]: "api",
  };

  const featureDescriptions = Object.values(
    t(`common:subscriptionProductsCardsLabels:${planMap[product.name]}`, {
      returnObjects: true,
    })
  );

  return featureDescriptions;
};

export const getAllFeatureLabels = (
  product: SubscriptionProduct,
  { t, i18n }: { t: TFunction; i18n: i18n }
): { key: string; label: string }[] => {
  const validFeatures: { key: string; label: string }[] = [];

  const planMap = {
    [SubscriptionProductNamesEnum.BASIC]: "basic",
    [SubscriptionProductNamesEnum.PRO]: "pro",
    [SubscriptionProductNamesEnum.EXCHANGE]: "exchange",
    [SubscriptionProductNamesEnum.API]: "api",
  };

  if (product.name === "Basic") {
    Object.values(DashBaseFeaturesEnum).forEach((baseFeature) => {
      validFeatures.push({
        key: baseFeature,
        label: t(
          `common:featureLabels:${planMap[product.name]}:${camelCase(
            baseFeature
          )}`
        ),
      });
    });
    return validFeatures;
  }

  product.features.forEach((feature) => {
    const labelKey = `common:featureLabels:${planMap[product.name]}:${camelCase(
      feature.feature
    )}`;
    const labelExists = i18n.exists(labelKey);

    if (feature.active && labelExists) {
      validFeatures.push({
        key: feature.feature,
        label: t(labelKey),
      });
    }
  });

  return validFeatures;
};

export const getFeatureLabelsInfoToCard = (
  product: SubscriptionProduct,
  options: { t: TFunction; i18n: i18n; ignoreTrial?: boolean }
) => {
  const validFeatures = getAllFeatureLabels(product, options);

  return {
    featureLabels: validFeatures.slice(0, MAX_FEATURE_ITEMS_NUMBER),
    showSeeMore: validFeatures.length > SHOW_SEE_MORE_FROM_ITEMS_NUMBER,
  };
};

export const getStatusBadgesInfo = (
  status: ProductSubscriptionStatusesEnum,
  isDeleted: boolean,
  { t }: { t: TFunction }
): { type: BadgeTypes; title: string }[] => {
  const badges: { type: BadgeTypes; title: string }[] = [];
  const statusText = t(`common:subscriptionStatuses.${status}`);

  if (
    status !== ProductSubscriptionStatusesEnum.SCHEDULED_CANCELLATION &&
    SUBSCRIPTION_ACTIVE_STATUSES.includes(status)
  ) {
    badges.push({ type: "active", title: statusText });
  } else {
    badges.push({ type: "danger", title: statusText });
  }

  if (isDeleted) {
    badges.push({
      type: "alert",
      title: t("pages:profilePage.billing.deleted"),
    });
  }

  return badges;
};

export const checkIfAllowedPlanChangeRadio = (
  subscription?: ProductSubscriptionModel | null
): boolean => {
  return (
    subscription?.status !==
      ProductSubscriptionStatusesEnum.SCHEDULED_CANCELLATION &&
    subscription?.status !== ProductSubscriptionStatusesEnum.TRIALING
  );
};

export const checkIfAllowedPlanChange = (
  subscription?: ProductSubscriptionModel | null
): boolean => {
  if (
    subscription?.status ===
      ProductSubscriptionStatusesEnum.SCHEDULED_CANCELLATION ||
    subscription?.status === ProductSubscriptionStatusesEnum.TRIALING ||
    subscription?.status === ProductSubscriptionStatusesEnum.PAST_DUE ||
    subscription?.status === ProductSubscriptionStatusesEnum.INCOMPLETE ||
    subscription?.status === ProductSubscriptionStatusesEnum.INCOMPLETE_EXPIRED
  ) {
    return false;
  }

  return true;
};
